import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useLangMap } from '../../hooks/useLanguages';
import { fetchData } from '../../services/dataApi';
import { localizeExercisePack } from '../../services/exerciseLocalization';
import { XpService } from '../../services/XpService';
import { LearningProgressService } from '../../services/LearningProgressService';
import { SoundEffectService } from '../../services/SoundEffectService';
import { ExerciseScoreService } from '../../services/ExerciseScoreService';
import { ensureDailyTestSkillModes } from '../../services/DailyTestQuestionService';
import { ChunkyButton, GameProgressBar, FeedbackDrawer, Confetti, StarRow } from '../../components/GameUI';

const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
const normalize = (value = '') => String(value).toLowerCase().replace(/[^\p{L}\p{N}\s]/gu, '').replace(/\s+/g, ' ').trim();
const speechScore = (expected, heard) => {
  const wanted = normalize(expected).split(' ').filter(Boolean);
  const actual = new Set(normalize(heard).split(' ').filter(Boolean));
  return wanted.length ? Math.round(wanted.filter((word) => actual.has(word)).length / wanted.length * 100) : 0;
};

// Duolingo-esque unit palette, cycled by level index.
const LEVEL_COLORS = [
  ['#58CC02', '#4CAD02'],
  ['#1CB0F6', '#0A94D6'],
  ['#FF9600', '#E08600'],
  ['#CE82FF', '#B25CF0'],
  ['#FF4B4B', '#D93A3A'],
];

function LevelCard({ index, exercise, bestPercent, onTap, t }) {
  const [color, shadowColor] = LEVEL_COLORS[index % LEVEL_COLORS.length];
  const stars = ExerciseScoreService.starsForPercent(bestPercent);
  return (
    <button
      type="button"
      onClick={onTap}
      className="flex w-full items-start gap-3.5 rounded-[22px] border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
    >
      <div
        className="flex shrink-0 items-center justify-center rounded-full text-xl font-black text-white"
        style={{
          width: 52, height: 52,
          background: `linear-gradient(135deg, ${color}, ${shadowColor})`,
          boxShadow: `0 3px 10px ${color}66`,
        }}
      >
        {index + 1}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate font-black text-slate-900">{exercise.title || t('exercise.defaultTitle')}</p>
        <p className="mt-0.5 text-sm font-bold text-violet-700">{exercise.theme || exercise.description}</p>
        <p className="line-clamp-2 text-xs text-slate-500">{exercise.focus || exercise.description}</p>
        <div className="mt-2 flex items-center gap-2">
          <StarRow count={stars} size={16} />
          <span className="truncate text-[10px] font-extrabold" style={{ color }}>
            {t('exercise.cardMeta', { level: exercise.level || '', count: exercise.questions.length, xp: exercise.xp || 0 })}
          </span>
        </div>
      </div>
      <ChunkyButton color={color} shadowColor={shadowColor} depth={4} onClick={onTap} className="shrink-0 px-4 py-2.5 text-white">
        ▶
      </ChunkyButton>
    </button>
  );
}

function ComingSoon({ t }) {
  return (
    <div className="mt-10 flex flex-col items-center rounded-3xl border border-slate-200 bg-white p-10 text-center">
      <p className="text-5xl">🧪</p>
      <h2 className="mt-3 text-xl font-black">{t('exercise.comingSoon')}</h2>
      <p className="text-slate-500">{t('exercise.comingSoonDesc')}</p>
    </div>
  );
}

function StatBlock({ label, value, color }) {
  return (
    <div className="flex flex-col items-center">
      <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">{label}</p>
      <p className="mt-1 text-2xl font-black" style={{ color }}>{value}</p>
    </div>
  );
}

function CompletionView({ color, score, total, xp, goalJustReached, onDone, t }) {
  const percent = total === 0 ? 0 : Math.round((score / total) * 100);
  const perfect = total > 0 && score === total;
  const stars = ExerciseScoreService.starsForPercent(percent);
  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center bg-white px-6 py-10 text-center">
      {perfect && <Confetti />}
      <p className="text-7xl">{perfect ? '🏆' : score >= total / 2 ? '🎉' : '💪'}</p>
      <h1 className="mt-4 text-3xl font-black text-slate-900">{t('exercise.testComplete')}</h1>
      {perfect && <p className="mt-1.5 text-sm font-extrabold text-amber-600">{t('exercise.perfectScore')}</p>}
      <div className="mt-5">
        <StarRow count={stars} size={36} />
      </div>
      <div className="mt-6 flex items-center gap-0 rounded-[20px] border border-slate-200 bg-slate-50 px-7 py-4">
        <StatBlock label={t('exercise.scoreLabel')} value={`${score}/${total}`} color={color} />
        <div className="mx-6 h-9 w-px bg-slate-200" />
        <StatBlock label={t('exercise.xpLabel')} value={`+${xp}`} color="#D97706" />
      </div>
      {goalJustReached && (
        <div className="mt-3.5 rounded-full border border-emerald-200 bg-emerald-50 px-3.5 py-2 text-xs font-extrabold text-emerald-700">
          {t('exercise.dailyGoalReached')}
        </div>
      )}
      <div className="mt-10 w-full max-w-sm">
        <ChunkyButton color={color} onClick={onDone} className="w-full py-4 text-center text-white">
          {t('exercise.backToTests')}
        </ChunkyButton>
      </div>
    </div>
  );
}

function OptionPill({ label, chosen, disabled, isAnswer, color, onClick }) {
  let bg = '#FFFFFF', border = '#E2E8F0', fg = '#1E293B';
  if (disabled) {
    if (isAnswer) { bg = '#D7FFB8'; border = '#58CC02'; fg = '#2B6B00'; }
    else if (chosen) { bg = '#FFDFE0'; border = '#FF4B4B'; fg = '#8B0000'; }
    else { bg = '#F8FAFC'; border = '#F1F5F9'; fg = '#CBD5E1'; }
  } else if (chosen) {
    bg = `${color}1F`; border = color; fg = color;
  }
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className="mb-2.5 flex w-full items-center gap-3 rounded-2xl px-4 py-3.5 text-left transition-all"
      style={{ background: bg, border: `2px solid ${border}`, cursor: disabled ? 'default' : 'pointer' }}
    >
      <div
        className="flex shrink-0 items-center justify-center rounded-full text-xs text-white"
        style={{ width: 26, height: 26, background: chosen || (disabled && isAnswer) ? border : 'transparent', border: `2px solid ${border}` }}
      >
        {disabled && isAnswer ? '✓' : disabled && chosen ? '✕' : ''}
      </div>
      <span className="min-w-0 flex-1 truncate text-sm font-bold" style={{ color: fg }}>{label}</span>
    </button>
  );
}

function QuestionCard({ question: q, langCode, ttsCode, targetDir, nativeDir, color, onMarked, t }) {
  const [selected, setSelected] = useState(new Set());
  const [matches, setMatches] = useState({});
  const [arranged, setArranged] = useState([]);
  const [dragLeftId, setDragLeftId] = useState(null);
  const [text, setText] = useState('');
  const [transcript, setTranscript] = useState('');
  const [recording, setRecording] = useState(false);
  const [correct, setCorrect] = useState(null);
  const recognition = useRef(null);
  const mountedRef = useRef(true);
  const options = Object.entries(q.options || {});
  const isSpeaking = q.speakPractice || q.type === 'speaking';
  const isMatch = q.type === 'match';
  const isMulti = q.type === 'multiple_select';
  const isArrange = q.type === 'arrange_sentence';
  const isText = q.translationPractice || q.type === 'translation' || q.type === 'fill_blank';
  const disabled = correct !== null;

  const correctAnswerText = isMatch
    ? (q.left || []).map((left) => `${left.text} → ${(q.right || []).find((right) => right.id === q.answer?.[left.id])?.text || ''}`).join(' · ')
    : isMulti
      ? (q.answer || []).map((key) => q.options?.[key]).filter(Boolean).join(', ')
      : isArrange
        ? q.answerText || q.explanation
      : isSpeaking
        ? q.expectedText || q.sentence
        : isText
          ? q.expectedText || q.answer
          : q.options?.[q.answer] || q.answer;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      recognition.current?.abort();
    };
  }, []);

  const mark = (value) => {
    if (correct !== null) return;
    value ? SoundEffectService.playCorrect() : SoundEffectService.playWrong();
    setCorrect(value);
    const explanation = q.explanation?.toString().trim() || null;
    onMarked(value, value ? null : correctAnswerText, explanation);
  };

  const check = () => {
    if (isMatch) {
      const answer = q.answer || {};
      mark(Object.entries(answer).every(([left, right]) => matches[String(left)] === String(right)));
    } else if (isMulti) {
      const expected = [...(q.answer || [])].sort().join('|');
      mark([...selected].sort().join('|') === expected);
    } else if (isArrange) {
      mark(arranged.join('|') === (q.answer || []).map(String).join('|'));
    } else if (isText) {
      mark(normalize(text) === normalize(q.expectedText || q.answer));
    } else {
      mark(selected.has(q.answer));
    }
  };

  const assignMatch = (leftId, rightId) => {
    if (disabled) return;
    setMatches((old) => {
      const next = Object.fromEntries(Object.entries(old).filter(([, r]) => r !== rightId));
      next[leftId] = rightId;
      return next;
    });
  };

  const startSpeaking = () => {
    if (!SpeechRecognitionAPI || disabled) return;
    const instance = new SpeechRecognitionAPI();
    instance.lang = ttsCode || langCode;
    instance.interimResults = true;
    const transcriptRef = { current: '' };
    instance.onresult = (event) => {
      transcriptRef.current = Array.from(event.results).map((item) => item[0].transcript).join('');
      if (!mountedRef.current) return;
      setTranscript(transcriptRef.current);
    };
    instance.onend = () => {
      if (!mountedRef.current) return;
      setRecording(false);
      const score = speechScore(q.expectedText || q.sentence, transcriptRef.current);
      mark(score >= 70);
    };
    recognition.current = instance;
    setTranscript('');
    setRecording(true);
    instance.start();
  };

  const playListening = () => {
    const speakAudioText = () => {
      if (!q.audioText || !window.speechSynthesis) return;
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(q.audioText);
      utterance.lang = ttsCode || langCode;
      window.speechSynthesis.speak(utterance);
    };

    if (q.audio) {
      const audio = new Audio(`${process.env.PUBLIC_URL}/audio/${langCode}/${q.audio}`);
      audio.play().catch(speakAudioText);
      return;
    }
    speakAudioText();
  };

  return (
    <div className="mb-3 rounded-[22px] border border-slate-100 bg-white" style={{ padding: 18 }}>
      <span dir={nativeDir} className="inline-block rounded-full px-2.5 py-1 text-[11px] font-extrabold" style={{ background: `${color}1A`, color }}>
        {q.category || q.type}
      </span>
      <h3 dir={nativeDir} className="mt-2.5 text-lg font-black text-slate-900">{q.question}</h3>
      {(q.emoji || q.image) && (
        <div className="mt-3 flex min-h-28 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-50 to-orange-50">
          {q.image
            ? <img src={q.image} alt="" className="max-h-32 max-w-full object-contain" />
            : <span className="text-7xl" aria-hidden="true">{q.emoji}</span>}
        </div>
      )}
      {q.sentence && (
        <p dir={targetDir} className="mt-2.5 rounded-2xl bg-slate-50 p-3.5 text-xl font-bold text-slate-700">{q.sentence}</p>
      )}

      {q.listenQuiz && (
        <div className="mt-3.5">
          <ChunkyButton color={color} onClick={playListening} className="w-full py-3.5 text-center text-white">
            ▶ {t('exercise.playAudio')}
          </ChunkyButton>
        </div>
      )}

      {isSpeaking ? (
        <div className="mt-3.5">
          <ChunkyButton
            color={recording ? '#FF4B4B' : color}
            shadowColor={recording ? '#D93A3A' : undefined}
            disabled={disabled}
            onClick={recording ? () => recognition.current?.stop() : startSpeaking}
            className="w-full py-3.5 text-center text-white"
          >
            {recording ? `⏹ ${t('exercise.stopAndCheck')}` : `🎙️ ${t('exercise.startSpeaking')}`}
          </ChunkyButton>
          {transcript && <p className="mt-2.5 text-sm italic text-slate-500">{t('exercise.youSaid', { text: transcript })}</p>}
        </div>
      ) : isMatch ? (
        <div className="mt-3.5">
          <p className="text-xs font-semibold text-slate-500">{t('exercise.dragInstructions')}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {(q.left || []).map((left) => {
              const assigned = Object.prototype.hasOwnProperty.call(matches, left.id);
              const selected = dragLeftId === left.id;
              const selectThis = () => { if (!disabled) setDragLeftId(selected ? null : left.id); };
              return (
                <div
                  key={left.id}
                  dir={targetDir}
                  role="button"
                  tabIndex={disabled ? -1 : 0}
                  aria-pressed={selected}
                  draggable={!disabled}
                  onDragStart={(e) => { e.dataTransfer.setData('text/plain', String(left.id)); e.dataTransfer.effectAllowed = 'move'; setDragLeftId(left.id); }}
                  onClick={selectThis}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectThis(); } }}
                  className="flex cursor-grab items-center gap-2 rounded-xl px-3.5 py-2.5 text-sm font-extrabold active:cursor-grabbing"
                  style={{
                    background: assigned ? `${color}29` : `${color}14`,
                    border: `2px solid ${selected ? color : assigned ? `${color}99` : `${color}4D`}`,
                    boxShadow: selected ? `0 0 0 2px ${color}66` : undefined,
                    color: '#1E293B',
                    cursor: disabled ? 'default' : 'grab',
                  }}
                >
                  <span aria-hidden="true">{assigned ? '✓' : '⠿'}</span>{left.text}
                </div>
              );
            })}
          </div>
          <div className="mt-3.5 space-y-2">
            {(q.right || []).map((right) => {
              const assignedLeftId = Object.keys(matches).find((leftId) => matches[leftId] === String(right.id));
              const assignedLeft = (q.left || []).find((left) => String(left.id) === assignedLeftId);
              const pairCorrect = disabled && String(q.answer?.[assignedLeftId]) === String(right.id);
              const placeSelected = () => { if (!disabled && dragLeftId != null) { assignMatch(dragLeftId, String(right.id)); setDragLeftId(null); } };
              return (
                <div
                  key={right.id}
                  role="button"
                  tabIndex={disabled ? -1 : 0}
                  onDragOver={(e) => { if (!disabled) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; } }}
                  onDrop={(e) => { e.preventDefault(); assignMatch(e.dataTransfer.getData('text/plain') || dragLeftId, String(right.id)); }}
                  onClick={placeSelected}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); placeSelected(); } }}
                  className="grid min-h-14 items-center gap-2 rounded-2xl border-2 border-dashed p-3.5 transition sm:grid-cols-2"
                  style={{
                    background: disabled ? (pairCorrect ? '#ECFDF5' : '#FEF2F2') : assignedLeft ? '#F8FAFC' : '#FFFFFF',
                    borderColor: disabled ? (pairCorrect ? '#10B981' : '#F87171') : assignedLeft ? '#94A3B8' : '#CBD5E1',
                    cursor: disabled ? 'default' : 'pointer',
                  }}
                >
                  <span dir={nativeDir} className="text-sm font-bold text-slate-700">{right.text}</span>
                  <span className="text-sm font-bold sm:text-right" style={{ color: assignedLeft ? color : '#94A3B8' }}>
                    {assignedLeft?.text || t('exercise.dropHere')}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ) : isArrange ? (
        <div dir={targetDir} className="mt-3.5 space-y-3">
          <div className="min-h-16 rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 p-3">
            <div className="flex flex-wrap gap-2">
              {arranged.length === 0 && (
                <span className="text-sm font-semibold text-slate-400">{t('exercise.dropHere')}</span>
              )}
              {arranged.map((tokenId) => {
                const token = (q.tokens || []).find((item) => String(item.id) === tokenId);
                return (
                  <button
                    type="button"
                    key={tokenId}
                    disabled={disabled}
                    onClick={() => setArranged((old) => old.filter((id) => id !== tokenId))}
                    className="rounded-xl bg-white px-3 py-2 text-sm font-extrabold shadow-sm ring-1 ring-slate-200"
                    style={{ color }}
                  >
                    {token?.text}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {(q.tokens || []).filter((token) => !arranged.includes(String(token.id))).map((token) => (
              <button
                type="button"
                key={token.id}
                disabled={disabled}
                onClick={() => setArranged((old) => [...old, String(token.id)])}
                className="rounded-xl border-2 border-slate-200 bg-white px-3 py-2 text-sm font-extrabold text-slate-700 transition hover:border-slate-300"
              >
                {token.text}
              </button>
            ))}
          </div>
        </div>
      ) : isText ? (
        <input
          disabled={disabled}
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="mt-3.5 w-full rounded-2xl border-none bg-slate-50 p-3.5 font-semibold outline-none focus:ring-2"
          style={{ boxShadow: `0 0 0 0px ${color}` }}
          placeholder={t('exercise.typeAnswer')}
        />
      ) : (
        <div dir={q.optionsLanguage === 'native' ? nativeDir : targetDir} className="mt-3.5">
          {options.map(([key, value]) => {
            const chosen = selected.has(key);
            const isAnswer = isMulti ? (q.answer || []).map(String).includes(key) : q.answer?.toString() === key;
            return (
              <OptionPill
                key={key}
                label={value}
                chosen={chosen}
                disabled={disabled}
                isAnswer={isAnswer}
                color={color}
                onClick={() => setSelected((old) => {
                  const next = new Set(isMulti ? old : []);
                  if (next.has(key)) next.delete(key); else next.add(key);
                  return next;
                })}
              />
            );
          })}
        </div>
      )}

      {!isSpeaking && !disabled && (
        <div className="mt-1">
          <ChunkyButton
            color={color}
            disabled={isMatch
              ? Object.keys(matches).length < (q.left || []).length
              : isArrange
                ? arranged.length < (q.tokens || []).length
                : isText
                  ? !text.trim()
                  : selected.size === 0}
            onClick={check}
            className="w-full py-3.5 text-center text-white"
          >
            {t('exercise.checkAnswer')}
          </ChunkyButton>
        </div>
      )}
    </div>
  );
}

function ExerciseRunner({ exercise, langCode, ttsCode, targetDir, nativeDir, langPair, color, onExit }) {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [lastCorrect, setLastCorrect] = useState(null);
  const [lastCorrectAnswer, setLastCorrectAnswer] = useState(null);
  const [lastExplanation, setLastExplanation] = useState(null);
  const [finished, setFinished] = useState(false);
  const [goalJustReached, setGoalJustReached] = useState(false);
  const [earnedXp, setEarnedXp] = useState(0);
  const scoreRef = useRef(0);
  const praiseIdxRef = useRef(0);
  const questions = exercise.questions;
  const total = questions.length;

  const handleMarked = (correct) => {
    setLastCorrect(correct);
    if (correct) {
      scoreRef.current += 1;
      setScore(scoreRef.current);
      praiseIdxRef.current = Math.floor(Math.random() * 4);
    }
  };

  const finish = async () => {
    if (total > 0 && scoreRef.current === total) SoundEffectService.playCelebration();
    const maxXp = exercise.xp || 0;
    const xp = Math.round((maxXp * scoreRef.current) / total);
    setEarnedXp(xp);
    const xpResult = await XpService.addXp(xp);
    setGoalJustReached(!!xpResult?.goalJustReached);
    await LearningProgressService.record(
      langPair,
      'tests',
      Math.max(1, scoreRef.current),
      { activityId: `pack:${exercise._packId || '0'}` },
    );
    const percent = Math.round((scoreRef.current / total) * 100);
    ExerciseScoreService.recordResult(langCode, exercise._packId, percent);
  };

  const continueNext = async () => {
    if (index + 1 >= total) {
      setFinished(true);
      await finish();
    } else {
      setIndex((i) => i + 1);
      setLastCorrect(null);
      setLastCorrectAnswer(null);
      setLastExplanation(null);
    }
  };

  if (finished) {
    return (
      <CompletionView
        color={color}
        score={scoreRef.current}
        total={total}
        xp={earnedXp}
        goalJustReached={goalJustReached}
        onDone={() => onExit(Math.round((scoreRef.current / total) * 100))}
        t={t}
      />
    );
  }

  const q = questions[index];

  return (
    <div className="min-h-screen bg-white">
      <div className="flex items-center gap-3 px-3 py-2">
        <button type="button" onClick={() => onExit(null)} className="p-2 text-xl text-slate-400">✕</button>
        <div className="flex-1"><GameProgressBar value={total === 0 ? 0 : index / total} color={color} /></div>
        <span className="flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1.5 text-xs font-black text-amber-800">
          ⚡ {score}
        </span>
      </div>
      <div className="mx-auto max-w-2xl px-4" style={{ paddingBottom: lastCorrect !== null ? 160 : 16 }}>
        <QuestionCard
          key={q.id ?? index}
          question={q}
          langCode={langCode}
          ttsCode={ttsCode}
          targetDir={targetDir}
          nativeDir={nativeDir}
          color={color}
          onMarked={(correct, correctAnswer, explanation) => {
            handleMarked(correct);
            setLastCorrectAnswer(correctAnswer);
            setLastExplanation(explanation);
          }}
          t={t}
        />
      </div>
      {lastCorrect !== null && (
        <FeedbackDrawer
          correct={lastCorrect}
          title={lastCorrect ? t('game.praises', { returnObjects: true })[praiseIdxRef.current] : t('game.incorrectTitle')}
          subtitle={lastCorrect ? lastExplanation : (lastCorrectAnswer ? t('game.correctAnswerLabel', { answer: lastCorrectAnswer }) : lastExplanation)}
          continueLabel={t('game.continue')}
          onContinue={continueNext}
        />
      )}
    </div>
  );
}

export default function ExercisePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { languagePrefs } = useAuth();
  const { langMap } = useLangMap();
  const code = languagePrefs?.targetLanguage || 'en';
  const nativeCode = languagePrefs?.nativeLanguage || 'en';
  const pair = languagePrefs ? `${languagePrefs.nativeLanguage}_${code}` : `en_${code}`;
  const [exercises, setExercises] = useState(null);
  const [bestPercents, setBestPercents] = useState({});
  const [active, setActive] = useState(null);

  useEffect(() => {
    let isCurrent = true;
    setExercises(null);
    const loadLocale = async () => {
      const english = await fetchData('exercise/i18n/en.json');
      if (nativeCode === 'en') return english;
      const localized = await fetchData(`exercise/i18n/${nativeCode}.json`)
        .catch(() => null);
      if (!localized) return english;
      return {
        ...english,
        ...localized,
        strings: { ...(english.strings || {}), ...(localized.strings || {}) },
        terms: { ...(english.terms || {}), ...(localized.terms || {}) },
      };
    };
    Promise.all([
      loadLocale(),
      fetchData(`exercise/${code}/index.json?curriculum=30-day-v2`)
        .catch(() => Array.from({ length: 30 }, (_, offset) => {
          const id = offset + 1;
          return { id, file: `${id}.json` };
        })),
    ])
      .then(async ([locale, index]) => {
        const items = await Promise.all(index.map((entry) => {
          const id = String(entry.id);
          const file = entry.file || `${id}.json`;
          return fetchData(`exercise/${code}/${file}?curriculum=30-day-v2`)
            .then((data) => ({ ...data, _packId: id }))
            .catch(() => null);
        }));
        if (!isCurrent) return;
        const list = items.filter((item) => item && Object.keys(item).length && Array.isArray(item.questions) && item.questions.length);
        setExercises(list.map((item) => ensureDailyTestSkillModes(
          localizeExercisePack(item, locale),
          {
            listenPrompt: t('exercise.playAudio'),
            speakPrompt: t('exercise.startSpeaking'),
            translatePrompt: t('exercise.typeAnswer'),
          },
        )));
        setBestPercents(ExerciseScoreService.bestPercentsFor(code, list.map((e) => e._packId)));
      })
      .catch(() => { if (isCurrent) setExercises([]); });
    return () => { isCurrent = false; };
  }, [code, nativeCode, t]);

  const ttsCode = langMap[code]?.tts || code;

  const openExercise = (exercise, index) => setActive({ exercise, index });

  const onExit = (percent) => {
    if (percent !== null && active) {
      setBestPercents((old) => ({
        ...old,
        [active.exercise._packId]: Math.max(old[active.exercise._packId] ?? -1, percent),
      }));
    }
    setActive(null);
  };

  const activeColor = useMemo(
    () => active ? LEVEL_COLORS[active.index % LEVEL_COLORS.length][0] : null,
    [active],
  );

  if (active) {
    return (
      <ExerciseRunner
        exercise={active.exercise}
        langCode={code}
        ttsCode={ttsCode}
        targetDir={langMap[code]?.dir || 'ltr'}
        nativeDir={langMap[nativeCode]?.dir || 'ltr'}
        langPair={pair}
        color={activeColor}
        onExit={onExit}
      />
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-10">
      <div className="mx-auto max-w-4xl">
        <button onClick={() => navigate(-1)} className="text-sm font-bold text-slate-500">← {t('exercise.back')}</button>
        <p className="mt-6 text-xs font-black uppercase tracking-widest text-indigo-600">{t('exercise.practiceAndImprove')}</p>
        <h1 className="text-3xl font-black text-slate-900">{t('exercise.dailyTests')} · {langMap[code]?.label || code.toUpperCase()}</h1>
        <p className="mt-2 text-slate-500">{t('exercise.subtitle')}</p>
        {exercises === null ? (
          <div className="mt-10 h-32 animate-pulse rounded-3xl bg-slate-200" />
        ) : exercises.length === 0 ? (
          <ComingSoon t={t} />
        ) : (
          <div className="mt-8 space-y-3.5">
            {exercises.map((exercise, index) => (
              <LevelCard
                key={exercise._packId}
                index={index}
                exercise={exercise}
                bestPercent={bestPercents[exercise._packId] ?? -1}
                onTap={() => openExercise(exercise, index)}
                t={t}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
